import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Descriptions, Table, InputNumber, Button, Space, Tag, Select, Popconfirm, Spin, Empty, Tooltip, Typography, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import adminProductService from '../../services/adminProductService';
import StreamOpsPanel from '../../pages/tm/StreamOpsPanel';
import { TYPE_LABEL, STATUS_COLOR, STATUS_LABEL, CYCLE_LABEL } from './adminProductMeta';

const { Text } = Typography;

const fmtBytes = (b) => {
    if (b == null) return '-';
    if (b < 0) return '不限';
    const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let i = 0;
    let v = Number(b);
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
    return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
};
const fmtVal = (v, unit) => {
    if (v == null) return '-';
    if (Number(v) < 0) return '不限';
    return unit === 'bytes' ? fmtBytes(v) : String(v);
};

/**
 * 后台「管理」：一张订阅的用量、配额覆盖（custom_features）、附加包（赠送 / 取消）；邮件推送内嵌发信流运维面板。
 * 用在：用户详情「产品」行。
 */
const AdminProductManageDrawer = ({ open, subscriptionId, onClose, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({});
    const [addon, setAddon] = useState(null);
    const [qty, setQty] = useState(1);

    const load = useCallback(async () => {
        if (!subscriptionId) return;
        setLoading(true);
        const r = await adminProductService.manage(subscriptionId);
        setLoading(false);
        if (!r?.success) { message.error(r?.error || '加载失败'); return; }
        setData(r.data);
        setDraft(Object.fromEntries((r.data.overrides || []).map((o) => [o.key, o.override])));
        setAddon(null);
        setQty(1);
    }, [subscriptionId]);

    useEffect(() => { if (open) load(); else setData(null); }, [open, load]);

    const changed = (data?.overrides || []).filter((o) => (draft[o.key] ?? null) !== (o.override ?? null));

    const saveOverrides = async () => {
        setSaving(true);
        const r = await adminProductService.setOverrides(subscriptionId, Object.fromEntries(changed.map((o) => [o.key, draft[o.key] ?? null])));
        setSaving(false);
        if (r?.success) { message.success(r.queued === false ? '已保存（推送排队失败，稍后重试）' : '已保存'); load(); onChanged?.(); } else message.error(r?.error || '保存失败');
    };

    const grant = async () => {
        const r = await adminProductService.grantAddon(subscriptionId, addon, qty);
        if (r?.success) { message.success('已赠送'); load(); onChanged?.(); } else message.error(r?.error || '赠送失败');
    };

    const cancelAddon = async (childId) => {
        const r = await adminProductService.cancelAddon(childId);
        if (r?.success) { message.success('已取消'); load(); onChanged?.(); } else message.error(r?.error || '取消失败');
    };

    const s = data?.subscription;
    const ref = data?.usage?.ref || {};
    const ended = s && ['cancelled', 'terminated'].includes(s.status);

    return (
        <Drawer open={open} onClose={onClose} width={s?.type === 'tm' ? 780 : 640} destroyOnClose
            title={s ? `${TYPE_LABEL[s.type] || s.type} · ${s.product_name} #${s.id}` : '管理'}>
            <Spin spinning={loading}>
                {!data ? <Empty /> : (
                    <Space direction="vertical" size={20} style={{ width: '100%' }}>
                        <Descriptions size="small" column={2} bordered>
                            <Descriptions.Item label="状态"><Tag color={STATUS_COLOR[s.status] || 'default'}>{STATUS_LABEL[s.status] || s.status}</Tag></Descriptions.Item>
                            <Descriptions.Item label="周期">{CYCLE_LABEL[s.billing_cycle] || s.billing_cycle || '-'}</Descriptions.Item>
                            <Descriptions.Item label="到期">{s.expiry_date ? String(s.expiry_date).slice(0, 16) : '-'}</Descriptions.Item>
                            <Descriptions.Item label="资源">{ref.domain || ref.name || (ref.disk_org_id ? `组织 #${ref.disk_org_id}` : ref.disk_account_id ? `账号 #${ref.disk_account_id}` : '-')}</Descriptions.Item>
                        </Descriptions>

                        {(data.usage?.rows || []).length > 0 && (
                            <Table size="small" rowKey="label" pagination={false} dataSource={data.usage.rows}
                                columns={[
                                    { title: '用量', dataIndex: 'label' },
                                    { title: '已用', dataIndex: 'used', render: (v, r) => (v == null ? '-' : fmtVal(v, r.unit)) },
                                    { title: '上限', dataIndex: 'limit', render: (v, r) => fmtVal(v, r.unit) },
                                ]} />
                        )}

                        {(data.overrides || []).length > 0 && (
                            <div>
                                <Space style={{ marginBottom: 8 }}>
                                    <Text strong>配额覆盖</Text>
                                    <Tooltip title="留空 = 跟随套餐；-1 = 不限"><QuestionCircleOutlined style={{ color: '#999' }} /></Tooltip>
                                </Space>
                                <Table size="small" rowKey="key" pagination={false} dataSource={data.overrides}
                                    columns={[
                                        { title: '配额', dataIndex: 'label', render: (v, r) => `${v}（${r.unit}）` },
                                        { title: '套餐', dataIndex: 'plan', width: 90, render: (v) => fmtVal(v) },
                                        {
                                            title: '覆盖', key: 'ov', width: 150, render: (_, r) => (
                                                <InputNumber size="small" min={-1} precision={0} placeholder="跟随套餐" style={{ width: 130 }} disabled={ended}
                                                    value={draft[r.key] ?? null} onChange={(v) => setDraft((d) => ({ ...d, [r.key]: v ?? null }))} />
                                            ),
                                        },
                                        { title: '生效', dataIndex: 'effective', width: 80, render: (v) => fmtVal(v) },
                                    ]} />
                                <div style={{ textAlign: 'right', marginTop: 8 }}>
                                    <Button type="primary" size="small" disabled={!changed.length || ended} loading={saving} onClick={saveOverrides}>保存配额</Button>
                                </div>
                            </div>
                        )}

                        <div>
                            <Text strong>附加包</Text>
                            <div style={{ margin: '8px 0' }}>
                                {(data.addons || []).length === 0 ? <Text type="secondary">无</Text> : (
                                    <Space wrap>
                                        {data.addons.map((a) => (
                                            <Tag key={a.subscription_id} color={a.status === 'active' ? 'blue' : 'orange'}>
                                                {a.name}{a.quantity > 1 ? ` × ${a.quantity}` : ''}{a.granted ? ' · 赠送' : ''}
                                                <Popconfirm title="立即取消这个附加包？不自动退款" okText="取消附加包" cancelText="返回" onConfirm={() => cancelAddon(a.subscription_id)}>
                                                    <Button type="link" size="small" danger style={{ padding: '0 0 0 4px', height: 'auto' }}>取消</Button>
                                                </Popconfirm>
                                            </Tag>
                                        ))}
                                    </Space>
                                )}
                            </div>
                            {(data.addon_catalog || []).length > 0 && (
                                <Space wrap>
                                    <Select size="small" style={{ width: 220 }} placeholder="选择附加包" value={addon} onChange={setAddon}
                                        options={data.addon_catalog.map((a) => ({ value: a.product_id, label: a.name }))} />
                                    <InputNumber size="small" min={1} max={1000} precision={0} value={qty} onChange={(v) => setQty(v || 1)} addonBefore="×" style={{ width: 110 }} />
                                    <Tooltip title="不出账单，立即生效；随主套餐顺延，续费不收">
                                        <Button size="small" disabled={!addon} onClick={grant}>赠送</Button>
                                    </Tooltip>
                                </Space>
                            )}
                        </div>

                        {s.type === 'tm' && ref.tm_stream_id && <StreamOpsPanel streamId={ref.tm_stream_id} onChanged={onChanged} showStatusActions />}
                    </Space>
                )}
            </Spin>
        </Drawer>
    );
};

export default AdminProductManageDrawer;
